import { computed, Injectable, signal } from '@angular/core';
import Keycloak, { KeycloakConfig, KeycloakInitOptions, KeycloakProfile } from 'keycloak-js';

/**
 * Schlanker Wrapper um keycloak-js für das uVI-Portal (Realm "uvi",
 * Public Client "uvi-portal", PKCE — Einrichtung siehe
 * kmt-backend/deploy/KEYCLOAK-UVI.md).
 *
 * Bewusst ohne Rollen-/Gruppen-Logik: Portal-Tokens tragen keine
 * Fachlichkeit, die Identität (Kontakt + uVI-Verträge) liefert das Backend
 * über GET /portal/me (PortalService).
 */
@Injectable({
  providedIn: 'root',
})
export class KeycloakService {
  private keycloakInstance?: Keycloak;
  private readonly isAuthenticatedSignal = signal<boolean>(false);
  private readonly userProfileSignal = signal<KeycloakProfile | null>(null);

  readonly isAuthenticated = computed(() => this.isAuthenticatedSignal());
  readonly userProfile = computed(() => this.userProfileSignal());

  async init(config: KeycloakConfig, initOptions: KeycloakInitOptions): Promise<boolean> {
    this.keycloakInstance = new Keycloak(config);

    try {
      const authenticated = await this.keycloakInstance.init(initOptions);
      this.isAuthenticatedSignal.set(authenticated);

      if (authenticated) {
        await this.loadUserProfile();
        this.setupTokenRefresh();
      }

      return authenticated;
    } catch (error) {
      console.error('Keycloak initialization failed:', error);
      return false;
    }
  }

  private async loadUserProfile(): Promise<void> {
    if (!this.keycloakInstance) return;

    try {
      const profile = await this.keycloakInstance.loadUserProfile();
      this.userProfileSignal.set(profile);
    } catch (error) {
      console.error('Failed to load user profile:', error);
    }
  }

  private setupTokenRefresh(): void {
    if (!this.keycloakInstance) return;

    // Token alle 30 Sekunden aktualisieren wenn es in 60 Sekunden abläuft
    setInterval(() => {
      this.updateToken(60);
    }, 30000);
  }

  async login(redirectUri?: string): Promise<void> {
    if (!this.keycloakInstance) {
      throw new Error('Keycloak not initialized');
    }

    await this.keycloakInstance.login({
      redirectUri: redirectUri ?? window.location.origin,
    });
  }

  async logout(redirectUri?: string): Promise<void> {
    if (!this.keycloakInstance) {
      throw new Error('Keycloak not initialized');
    }

    await this.keycloakInstance.logout({
      redirectUri: redirectUri ?? window.location.origin,
    });
  }

  async updateToken(minValidity: number = 30): Promise<boolean> {
    if (!this.keycloakInstance) {
      return false;
    }

    try {
      return await this.keycloakInstance.updateToken(minValidity);
    } catch (error) {
      console.error('Failed to refresh token:', error);
      return false;
    }
  }

  /**
   * Liefert ein gültiges Access-Token und erneuert es vorher, falls es in
   * den nächsten 30 Sekunden abläuft. Ist die Session (Refresh-Token)
   * abgelaufen, wird zum Login umgeleitet.
   */
  async getValidToken(): Promise<string | undefined> {
    if (!this.keycloakInstance?.authenticated) {
      return undefined;
    }

    try {
      await this.keycloakInstance.updateToken(30);
    } catch {
      await this.login(window.location.href);
      return undefined;
    }

    return this.keycloakInstance.token;
  }

  /**
   * Erzwingt einen Token-Refresh (nach einem 401). Ist die Session
   * abgelaufen, wird zum Login umgeleitet.
   */
  async forceRefreshToken(): Promise<string | undefined> {
    if (!this.keycloakInstance?.authenticated) {
      return undefined;
    }

    try {
      await this.keycloakInstance.updateToken(-1);
      return this.keycloakInstance.token;
    } catch {
      await this.login(window.location.href);
      return undefined;
    }
  }

  getToken(): string | undefined {
    return this.keycloakInstance?.token;
  }

  accountManagement(): void {
    this.keycloakInstance?.accountManagement();
  }

  getInstance(): Keycloak | undefined {
    return this.keycloakInstance;
  }
}
